import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { useOverlayWS } from "../lib/useOverlayWS";
import { defaultTextElement, defaultMediaElement, defaultDynamicElement, defaultTwitchElement, defaultChatFeedElement, defaultCommentElement, defaultSuperChatElement, defaultShapeElement } from "../lib/elementStyle";
import { Canvas } from "../components/studio/Canvas";
import { MediaLibrary } from "../components/studio/MediaLibrary";
import { LayersPanel } from "../components/studio/LayersPanel";
import { PropertiesPanel, GroupPanel, MultiPanel } from "../components/studio/PropertiesPanel";
import { GroupDialog } from "../components/studio/GroupDialog";
import { groupSelId, groupNameOf, isGroupSel, membersOf, groupColorMap, groupAnimOf, movePatches } from "../lib/groups";
import { LivePanel } from "../components/studio/LivePanel";
import { SetupModal } from "../components/SetupModal";
import { YouTubePanel } from "../components/YouTubePanel";
import { TwitchModal } from "../components/TwitchModal";
import { FontsModal } from "../components/FontsModal";
import { OperatorModal } from "../components/OperatorModal";
import { FontFaceStyle } from "../components/FontFaceStyle";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { SheetsModal } from "../components/SheetsModal";
import { usePermissions } from "../lib/usePermissions";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { ArrowLeft, Copy, RefreshCw, HelpCircle, Type, Check, Loader2, Cloud, ExternalLink, Smartphone, MessagesSquare, Quote, Sparkles, Baseline, Headphones, Sheet, Ratio, MonitorPlay, X, Circle, Triangle, Star, Square, Diamond, Shapes, ChevronDown } from "lucide-react";
import { t, tx } from "../i18n";
import { YouTubeIcon } from "../components/YouTubeIcon";
import { TwitchIcon } from "../components/TwitchIcon";

const SHAPE_ITEMS = [
  ["retangulo", Square, "Retângulo", "add-shape-rect-btn"],
  ["circulo", Circle, "Círculo", "add-shape-circle-btn"],
  ["triangulo", Triangle, "Triângulo", "add-shape-triangle-btn"],
  ["losango", Diamond, "Losango", "add-shape-diamond-btn"],
  ["estrela", Star, "Estrela", "add-shape-star-btn"],
];

const normalizeZ = (els) => els.map((e, i) => ({ ...e, zIndex: i }));

// Nome automático de textos: enquanto a camada não for renomeada à mão, o nome
// acompanha o conteúdo (1ª linha, até 40 caracteres). Renomear manualmente desliga isso.
const DEFAULT_TEXT_NAMES = ["Texto", "Text"];
function withAutoName(el, patch) {
  if (patch.nome !== undefined && patch.conteudo === undefined) return { ...patch, nomeAuto: false };
  if (patch.conteudo === undefined || el.tipo !== "texto" || el.papel) return patch;
  const auto = el.nomeAuto === true || (el.nomeAuto === undefined && DEFAULT_TEXT_NAMES.includes(el.nome));
  if (!auto) return patch;
  const first = String(patch.conteudo).split("\n").map((x) => x.trim()).find(Boolean) || "";
  const nome = first ? (first.length > 40 ? first.slice(0, 40) + "…" : first) : t("Texto");
  return { ...patch, nome, nomeAuto: true };
}

export default function Studio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [overlay, setOverlay] = useState(null);
  const [elements, setElements] = useState([]);
  const [media, setMedia] = useState({});
  const [library, setLibrary] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // Seleção múltipla (Shift+clique): ids selecionados; só vale com 2 ou mais.
  const [multiIds, setMultiIds] = useState([]);
  const select = (id) => { setSelectedId(id); setMultiIds((m) => (id && m.includes(id) ? m : [])); };
  const toggleMulti = (id) => {
    const base = multiIds.length ? multiIds : (selectedId && !isGroupSel(selectedId) ? [selectedId] : []);
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    if (next.length >= 2) { setMultiIds(next); setSelectedId(null); }
    else { setMultiIds([]); setSelectedId(next[0] || null); }
  };
  const [saving, setSaving] = useState("saved");
  const [sources, setSources] = useState(0);
  const [operatorsOnline, setOperatorsOnline] = useState(0); // links de operador abertos agora
  const [setupOpen, setSetupOpen] = useState(false);
  const [ytOpen, setYtOpen] = useState(false);
  const [ytData, setYtData] = useState(null);
  const [ytConfig, setYtConfig] = useState({ connected: false });
  const [twitchOpen, setTwitchOpen] = useState(false);
  const [twitchConfig, setTwitchConfig] = useState({ connected: false });
  const [twitchData, setTwitchData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(() => localStorage.getItem("studio_preview_open") === "1");
  const [previewPos, setPreviewPos] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("studio_preview_pos") || "null"); } catch { return null; }
  });
  const previewAreaRef = useRef(null);
  const previewRef = useRef(null);
  const [chatEnabled, setChatEnabled] = useState(false);
  const [chatDemo, setChatDemo] = useState(false);
  const [chatSource, setChatSource] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatPinned, setChatPinned] = useState(null);
  const [uploading, setUploading] = useState([]);
  const [fonts, setFonts] = useState([]);
  const { can } = usePermissions();
  const [fontsOpen, setFontsOpen] = useState(false);
  const [operatorOpen, setOperatorOpen] = useState(false);
  const [sheetsOpen, setSheetsOpen] = useState(false);
  const [sheetsConfig, setSheetsConfig] = useState({ connected: false });
  const [sheetValues, setSheetValues] = useState({});
  const saveTimer = useRef();
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const elementsRef = useRef([]);
  elementsRef.current = elements;
  const triggerKeys = useRef({});
  const [, forceTick] = useState(0);

  const { connected, send } = useOverlayWS(id, "panel", null, (msg) => {
    if (msg.type === "sources") { setSources(msg.count); setOperatorsOnline(msg.operators || 0); }
    else if (msg.type === "update_element") setElements((prev) => prev.map((e) => (e.id === msg.elementId ? { ...e, ...msg.patch } : e)));
    else if (msg.type === "state") setElements(msg.elements || []);
    else if (msg.type === "hide_all") setElements((prev) => prev.map((e) => ({ ...e, visivel: false })));
    else if (msg.type === "trigger") { triggerKeys.current[msg.elementId] = (triggerKeys.current[msg.elementId] || 0) + 1; forceTick((n) => n + 1); }
    else if (msg.type === "youtube_data") setYtData(msg.data);
    else if (msg.type === "twitch_data") setTwitchData(msg.data);
    else if (msg.type === "sheet_values") setSheetValues(msg.values || {});
    else if (msg.type === "chat_status") { setChatEnabled(msg.enabled); if (msg.demo !== undefined) setChatDemo(msg.demo); if (msg.source !== undefined) setChatSource(msg.source); if (!msg.enabled) setChatMessages([]); }
    else if (msg.type === "chat_state") { setChatMessages(msg.messages || []); setChatPinned(msg.pinned || null); }
    else if (msg.type === "chat_messages") {
      setChatMessages((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        const add = (msg.messages || []).filter((m) => !ids.has(m.id));
        return [...prev, ...add].slice(-60);
      });
    }
    else if (msg.type === "chat_reject") setChatMessages((prev) => prev.filter((x) => x.id !== msg.messageId));
    else if (msg.type === "chat_pin") setChatPinned(msg.message || null);
    else if (msg.type === "chat_error") toast.error(tx(msg.detail) || t("Erro no chat"));
  });

  // Load overlay + library
  useEffect(() => {
    api.get(`/overlays/${id}`).then(({ data }) => {
      setOverlay(data.overlay);
      setElements(data.overlay.elements || []);
      setMedia(data.media || {});
      setFonts(data.fonts || []);
    }).catch(() => { toast.error(t("Overlay não encontrado")); navigate("/dashboard"); });
  }, [id]);

  // Load this overlay's live connection config
  useEffect(() => {
    api.get(`/overlays/${id}/youtube`).then(({ data }) => {
      setYtConfig(data.youtube || { connected: false });
      if (data.data) setYtData(data.data);
    }).catch(() => {});
    api.get(`/overlays/${id}/chat`).then(({ data }) => {
      setChatEnabled(!!data.chat?.enabled);
      setChatDemo(!!data.chat?.demo);
      setChatSource(data.chat?.source || null);
      setChatMessages(data.messages || []);
      setChatPinned(data.pinned || null);
    }).catch(() => {});
    api.get(`/overlays/${id}/sheets`).then(({ data }) => {
      setSheetsConfig(data.sheets || { connected: false });
      if (data.values) setSheetValues(data.values);
    }).catch(() => {});
    api.get(`/overlays/${id}/twitch`).then(({ data }) => {
      setTwitchConfig(data.twitch || { connected: false });
      if (data.data) setTwitchData(data.data);
    }).catch(() => {});
  }, [id]);

  // Handle return from Google OAuth (private sheets)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const g = p.get("gsheets");
    if (!g) return;
    if (g === "connected") { toast.success(t("Google Sheets vinculado com sucesso")); setSheetsOpen(true); }
    else if (g === "denied") { toast.error(t("Permissão do Google Sheets negada. Autorize o acesso para usar planilhas privadas.")); setSheetsOpen(true); }
    else toast.error(t("Falha ao vincular o Google Sheets. Tente novamente."));
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const loadLibrary = useCallback(() => {
    api.get("/media").then(({ data }) => {
      setLibrary(data);
      setMedia((prev) => {
        const next = { ...prev };
        data.forEach((m) => { if (next[m.id]) next[m.id] = m; });
        return next;
      });
    });
  }, []);
  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  const loadFonts = useCallback(() => {
    api.get("/fonts").then(({ data }) => setFonts(data)).catch(() => {});
  }, []);

  // Poll while any media transcoding
  useEffect(() => {
    const pending = library.some((m) => m.status === "pendente" || m.status === "processando");
    if (!pending) return;
    const t = setInterval(loadLibrary, 3000);
    return () => clearInterval(t);
  }, [library, loadLibrary]);

  const scheduleSave = useCallback((els) => {
    setSaving("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/overlays/${id}`, { elements: els })
        .then(() => setSaving("saved"))
        .catch(() => setSaving("error"));
    }, 500);
  }, [id]);

  const pushHistory = (prev) => {
    undoStack.current.push(prev);
    if (undoStack.current.length > 50) undoStack.current.shift();
    redoStack.current = [];
  };

  const updateElement = useCallback((elId, patch, opts = {}) => {
    const { sendWs = true } = opts;
    setElements((prev) => {
      pushHistory(prev);
      const next = prev.map((e) => (e.id === elId ? { ...e, ...withAutoName(e, patch) } : e));
      scheduleSave(next);
      return next;
    });
    if (sendWs) send({ type: "update_element", elementId: elId, patch });
  }, [scheduleSave, send]);

  // Várias camadas de uma vez (grupos): um único passo de desfazer e um único envio.
  const updateElements = useCallback((patches) => {
    if (!patches || !Object.keys(patches).length) return;
    setElements((prev) => {
      pushHistory(prev);
      const next = prev.map((e) => (patches[e.id] ? { ...e, ...patches[e.id] } : e));
      scheduleSave(next);
      send({ type: "state", elements: next });
      return next;
    });
  }, [scheduleSave, send]);

  // ---- Grupos ----
  const [groupDlg, setGroupDlg] = useState(false);
  const uniqueGroupName = (base) => {
    const names = new Set(elementsRef.current.map((e) => e.grupo).filter(Boolean));
    let n = base, i = 2;
    while (names.has(n)) n = `${base} ${i++}`;
    return n;
  };
  const setGroupOf = (ids, name) => {
    const anim = name ? groupAnimOf(membersOf(elementsRef.current, name)) : null;
    updateElements(Object.fromEntries(ids.map((id) => [id, { grupo: name || null, grupoAnim: name ? anim : null }])));
  };
  const renameGroup = (oldName, newName) => {
    const target = membersOf(elementsRef.current, newName);
    const anim = target.length ? groupAnimOf(target) : groupAnimOf(membersOf(elementsRef.current, oldName));
    updateElements(Object.fromEntries(membersOf(elementsRef.current, oldName).map((m) => [m.id, { grupo: newName, grupoAnim: anim }])));
    setSelectedId(groupSelId(newName));
  };
  const ungroup = (name) => {
    updateElements(Object.fromEntries(membersOf(elementsRef.current, name).map((m) => [m.id, { grupo: null, grupoAnim: null }])));
    setSelectedId(null);
  };
  const setGroupAnim = (name, anim) => {
    updateElements(Object.fromEntries(membersOf(elementsRef.current, name).map((m) => [m.id, { grupoAnim: anim }])));
  };
  const pasteGroupCopy = (srcMembers, srcName) => {
    const prev = elementsRef.current;
    pushHistory(prev);
    const newName = uniqueGroupName(t("{nome} (cópia)", { nome: srcName }));
    const copies = srcMembers.map((m, i) => ({ ...JSON.parse(JSON.stringify(m)), id: crypto.randomUUID(), grupo: newName, x: (m.x || 0) + 24, y: (m.y || 0) + 24, zIndex: prev.length + i }));
    const next = normalizeZ([...prev, ...copies]);
    setElements(next);
    scheduleSave(next);
    send({ type: "state", elements: next });
    setSelectedId(groupSelId(newName));
  };
  const removeGroup = (name) => {
    const prev = elementsRef.current;
    pushHistory(prev);
    const next = normalizeZ(prev.filter((e) => e.grupo !== name));
    setElements(next);
    scheduleSave(next);
    send({ type: "state", elements: next });
    setSelectedId(null);
  };

  const addElement = (el) => {
    const prev = elementsRef.current;
    pushHistory(prev);
    const next = normalizeZ([...prev, { ...el, zIndex: prev.length }]);
    setElements(next);
    scheduleSave(next);
    send({ type: "state", elements: next });
    setSelectedId(el.id);
  };

  const duplicateElement = (elId) => {
    if (isGroupSel(elId)) {
      const name = groupNameOf(elId);
      pasteGroupCopy(membersOf(elementsRef.current, name), name);
      toast.success(t("Grupo duplicado"));
      return;
    }
    const src = elementsRef.current.find((e) => e.id === elId);
    if (!src) return;
    const clone = {
      ...JSON.parse(JSON.stringify(src)),
      id: crypto.randomUUID(),
      nome: t("{nome} (cópia)", { nome: src.nome || t("Elemento") }),
      x: (src.x || 0) + 24,
      y: (src.y || 0) + 24,
    };
    addElement(clone);
    toast.success(t("Elemento duplicado"));
  };

  const removeElement = (elId) => {
    const prev = elementsRef.current;
    pushHistory(prev);
    const next = normalizeZ(prev.filter((e) => e.id !== elId));
    setElements(next);
    scheduleSave(next);
    send({ type: "state", elements: next });
    setSelectedId((s) => (s === elId ? null : s));
  };

  const removeMany = (ids) => {
    const prev = elementsRef.current;
    pushHistory(prev);
    const next = normalizeZ(prev.filter((x) => !ids.includes(x.id)));
    setElements(next);
    scheduleSave(next);
    send({ type: "state", elements: next });
    setMultiIds([]);
    setSelectedId(null);
    toast.success(t("Camadas excluídas (Ctrl+Z para desfazer)"));
  };

  const reorder = (elId, dir) => {
    const prev = elementsRef.current;
    const sorted = [...prev].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    // Seleção múltipla: o bloco selecionado sobe/desce junto, uma posição por clique.
    if (multiIds.length > 1 && multiIds.includes(elId)) {
      const isSel = (e) => multiIds.includes(e.id);
      let moved = false;
      if (dir === "up") { for (let i = sorted.length - 2; i >= 0; i--) if (isSel(sorted[i]) && !isSel(sorted[i + 1])) { [sorted[i], sorted[i + 1]] = [sorted[i + 1], sorted[i]]; moved = true; } }
      else { for (let i = 1; i < sorted.length; i++) if (isSel(sorted[i]) && !isSel(sorted[i - 1])) { [sorted[i], sorted[i - 1]] = [sorted[i - 1], sorted[i]]; moved = true; } }
      if (!moved) return;
      pushHistory(prev);
      const nextM = normalizeZ(sorted);
      setElements(nextM);
      scheduleSave(nextM);
      send({ type: "state", elements: nextM });
      return;
    }
    const idx = sorted.findIndex((e) => e.id === elId);
    let swap = dir === "up" ? idx + 1 : idx - 1;
    // Camada de um grupo: as setas ordenam só dentro do grupo (troca com a vizinha do mesmo grupo).
    const grupo = sorted[idx]?.grupo;
    if (grupo) {
      const step = dir === "up" ? 1 : -1;
      swap = idx + step;
      while (swap >= 0 && swap < sorted.length && sorted[swap].grupo !== grupo) swap += step;
    }
    if (swap < 0 || swap >= sorted.length) return;
    pushHistory(prev);
    [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
    const next = normalizeZ(sorted);
    setElements(next);
    scheduleSave(next);
    send({ type: "state", elements: next });
  };

  // Move um grupo inteiro uma posição acima/abaixo da linha vizinha (outro grupo ou camada solta);
  // todas as camadas do grupo acompanham e a ordem interna delas é mantida.
  const reorderGroup = (name, dir) => {
    const prev = elementsRef.current;
    const asc = [...prev].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const rows = []; // de cima para baixo, como no painel de camadas
    const seen = new Set();
    for (const el of [...asc].reverse()) {
      if (!el.grupo) rows.push({ key: el.id, items: [el] });
      else if (!seen.has(el.grupo)) {
        seen.add(el.grupo);
        rows.push({ key: `g:${el.grupo}`, items: asc.filter((x) => x.grupo === el.grupo) });
      }
    }
    const idx = rows.findIndex((r) => r.key === `g:${name}`);
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || swap < 0 || swap >= rows.length) return;
    pushHistory(prev);
    [rows[idx], rows[swap]] = [rows[swap], rows[idx]];
    const next = normalizeZ([...rows].reverse().flatMap((r) => r.items));
    setElements(next);
    scheduleSave(next);
    send({ type: "state", elements: next });
  };

  const applyElements = (next) => {
    setElements(next);
    scheduleSave(next);
    send({ type: "state", elements: next });
  };
  const undo = () => {
    if (!undoStack.current.length) return;
    redoStack.current.push(elementsRef.current);
    const prev = undoStack.current.pop();
    applyElements(prev);
  };
  const redo = () => {
    if (!redoStack.current.length) return;
    undoStack.current.push(elementsRef.current);
    applyElements(redoStack.current.pop());
  };

  // Keyboard shortcuts
  const clipboardRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target.isContentEditable) return;
      const k = e.key.toLowerCase();
      const gName = groupNameOf(selectedId);
      if (multiIds.length > 1) {
        const ms = elementsRef.current.filter((x) => multiIds.includes(x.id));
        if (e.key === "Escape") { setMultiIds([]); return; }
        if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); removeMany(multiIds); return; }
        if (["arrowup", "arrowdown", "arrowleft", "arrowright"].includes(k) && !e.ctrlKey && !e.metaKey) {
          if (ms.some((m) => m.travado)) return;
          e.preventDefault();
          const step = e.shiftKey ? 10 : 1;
          updateElements(movePatches(ms, k === "arrowleft" ? -step : k === "arrowright" ? step : 0, k === "arrowup" ? -step : k === "arrowdown" ? step : 0));
          return;
        }
      }
      if ((e.ctrlKey || e.metaKey) && k === "g") {
        e.preventDefault();
        if (selectedId) setGroupDlg(true);
        else toast(t("Selecione uma camada ou um grupo para agrupar."));
        return;
      }
      if (gName) {
        const members = membersOf(elementsRef.current, gName);
        if ((e.ctrlKey || e.metaKey) && k === "c") {
          if (window.getSelection()?.toString()) return;
          e.preventDefault();
          clipboardRef.current = { group: gName, members: JSON.parse(JSON.stringify(members)) };
          toast.success(t("Grupo copiado (Ctrl+V para colar)"));
          return;
        }
        if ((e.key === "Delete" || e.key === "Backspace")) {
          e.preventDefault();
          removeGroup(gName);
          toast.success(t("Grupo excluído (Ctrl+Z para desfazer)"));
          return;
        }
        if (["arrowup", "arrowdown", "arrowleft", "arrowright"].includes(k) && !e.ctrlKey && !e.metaKey) {
          if (members.some((m) => m.travado)) return;
          e.preventDefault();
          const step = e.shiftKey ? 10 : 1;
          const dx = k === "arrowleft" ? -step : k === "arrowright" ? step : 0;
          const dy = k === "arrowup" ? -step : k === "arrowdown" ? step : 0;
          updateElements(movePatches(members, dx, dy));
          return;
        }
      }
      if ((e.ctrlKey || e.metaKey) && k === "v" && clipboardRef.current?.group) {
        e.preventDefault();
        pasteGroupCopy(clipboardRef.current.members, clipboardRef.current.group);
        toast.success(t("Grupo colado"));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && k === "z") {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      } else if ((e.ctrlKey || e.metaKey) && k === "c" && selectedId) {
        if (window.getSelection()?.toString()) return; // não interferir em cópia de texto
        const el = elementsRef.current.find((x) => x.id === selectedId);
        if (!el) return;
        e.preventDefault();
        clipboardRef.current = JSON.parse(JSON.stringify(el));
        toast.success(t("Elemento copiado (Ctrl+V para colar)"));
      } else if ((e.ctrlKey || e.metaKey) && k === "v" && clipboardRef.current) {
        e.preventDefault();
        const src = clipboardRef.current;
        const clone = {
          ...JSON.parse(JSON.stringify(src)),
          id: crypto.randomUUID(),
          nome: t("{nome} (cópia)", { nome: src.nome || t("Elemento") }),
          x: (src.x || 0) + 24,
          y: (src.y || 0) + 24,
        };
        addElement(clone);
        toast.success(t("Elemento colado"));
      } else if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        removeElement(selectedId);
      } else if (["arrowup", "arrowdown", "arrowleft", "arrowright"].includes(k) && selectedId && !e.ctrlKey && !e.metaKey) {
        const el = elementsRef.current.find((x) => x.id === selectedId);
        if (!el || el.travado) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const patch = {};
        if (k === "arrowup") patch.y = (el.y || 0) - step;
        else if (k === "arrowdown") patch.y = (el.y || 0) + step;
        else if (k === "arrowleft") patch.x = (el.x || 0) - step;
        else patch.x = (el.x || 0) + step;
        updateElement(selectedId, patch);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, multiIds]);

  // Uploads
  const handleUpload = async (files) => {
    for (const file of files) {
      const entry = { name: file.name, pct: 0 };
      setUploading((u) => [...u, entry]);
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post("/media/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded / (e.total || 1)) * 100);
            setUploading((u) => u.map((x) => (x.name === file.name ? { ...x, pct } : x)));
          },
        });
      } catch (err) {
        toast.error(t("Falha ao enviar {arquivo}: {erro}", { arquivo: file.name, erro: err.response?.data?.detail || "" }));
      } finally {
        setUploading((u) => u.filter((x) => x.name !== file.name));
      }
    }
    loadLibrary();
    toast.success(t("Upload concluído. Transcodificando…"));
  };

  const addMedia = (m) => {
    setMedia((prev) => ({ ...prev, [m.id]: m }));
    addElement(defaultMediaElement(m, elements.length));
  };
  const deleteMedia = async (mid) => { await api.delete(`/media/${mid}`); loadLibrary(); };
  const reprocessMedia = async (mid) => { await api.post(`/media/${mid}/reprocess`); loadLibrary(); };

  const publicUrl = overlay ? `${window.location.origin}/live/${overlay.id}?key=${overlay.token}` : "";
  const copyUrl = () => { navigator.clipboard.writeText(publicUrl); toast.success(t("URL copiada!")); };
  const startPreviewDrag = (e) => {
    const area = previewAreaRef.current, panel = previewRef.current;
    if (!area || !panel) return;
    const areaRect = area.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const offX = e.clientX - panelRect.left;
    const offY = e.clientY - panelRect.top;
    const onMove = (ev) => {
      let x = ev.clientX - areaRect.left - offX;
      let y = ev.clientY - areaRect.top - offY;
      x = Math.max(0, Math.min(x, areaRect.width - panelRect.width));
      y = Math.max(0, Math.min(y, areaRect.height - panelRect.height));
      setPreviewPos({ x, y });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setPreviewPos((p) => { if (p) sessionStorage.setItem("studio_preview_pos", JSON.stringify(p)); return p; });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    e.preventDefault();
  };
  const regenerate = async () => {
    const { data } = await api.post(`/overlays/${id}/regenerate-token`);
    setOverlay((o) => ({ ...o, token: data.token }));
    toast.success(t("Token regenerado. Atualize a URL no OBS."));
  };

  const changeFormat = async (w, h) => {
    await api.put(`/overlays/${id}`, { largura: w, altura: h });
    setOverlay((o) => ({ ...o, largura: w, altura: h }));
    toast.success(t("Formato {w}×{h}", { w, h }));
  };

  const hideAll = () => {
    const next = elementsRef.current.map((e) => ({ ...e, visivel: false }));
    setElements(next);
    scheduleSave(next);
    send({ type: "hide_all" });
  };

  const toggleGroup = (grupo, visible) => {
    const next = elementsRef.current.map((e) => (e.grupo === grupo ? { ...e, visivel: visible } : e));
    setElements(next);
    scheduleSave(next);
    elementsRef.current.forEach((e) => {
      if (e.grupo === grupo) send({ type: "update_element", elementId: e.id, patch: { visivel: visible } });
    });
  };

  // Chat curation handlers
  const startChat = async (demo, source) => {
    try { await api.post(`/overlays/${id}/chat/start`, { demo, source }); }
    catch (e) { toast.error(e.response?.data?.detail || t("Falha ao iniciar chat")); }
  };
  const stopChat = async () => { try { await api.post(`/overlays/${id}/chat/stop`); } catch {} };
  const pinMsg = (m) => { setChatPinned(m); send({ type: "chat_pin", message: m }); };
  const unpinMsg = () => { setChatPinned(null); send({ type: "chat_pin", message: null }); };
  const rejectMsg = (mid) => { setChatMessages((prev) => prev.filter((x) => x.id !== mid)); send({ type: "chat_reject", messageId: mid }); };
  const alertMsg = (m) => { send({ type: "chat_alert", message: m }); toast.success(t("Alerta enviado à tela")); };

  const selected = elements.find((e) => e.id === selectedId);
  const selGroupName = groupNameOf(selectedId);
  const selGroupMembers = selGroupName ? membersOf(elements, selGroupName) : [];
  const groupColors = groupColorMap(elements);
  // Se o grupo selecionado deixou de existir (desagrupado, excluído), limpa a seleção.
  useEffect(() => { if (selGroupName && !selGroupMembers.length) setSelectedId(null); }, [selGroupName, selGroupMembers.length]);
  const groups = [...new Set(elements.map((e) => e.grupo).filter(Boolean))];

  if (!overlay) {
    return <div className="h-screen flex items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      <FontFaceStyle fonts={fonts} />
      {/* Header */}
      <header className="h-14 border-b border-border/60 bg-card flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-foreground" data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></button>
          <input
            value={overlay.nome}
            onChange={(e) => setOverlay((o) => ({ ...o, nome: e.target.value }))}
            onBlur={(e) => api.put(`/overlays/${id}`, { nome: e.target.value })}
            className="bg-transparent font-display font-bold text-sm outline-none border-b border-transparent focus:border-primary min-w-0 max-w-[180px]"
            data-testid="overlay-name-edit"
          />
          <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="save-status">
            {saving === "saving" ? <><Cloud className="h-3.5 w-3.5" />{t("Salvando…")}</> : saving === "error" ? <span className="text-destructive">{t("Erro ao salvar")}</span> : <><Check className="h-3.5 w-3.5 text-emerald-400" />{t("Salvo")}</>}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-1 bg-black/40 border border-border rounded-sm pl-2 pr-1 h-8 max-w-xs">
            <span className="font-mono text-[11px] text-muted-foreground truncate max-w-[220px]">{publicUrl}</span>
            <button onClick={copyUrl} className="p-1 hover:text-primary text-muted-foreground" data-testid="copy-url-btn"><Copy className="h-3.5 w-3.5" /></button>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { const v = !previewOpen; setPreviewOpen(v); localStorage.setItem("studio_preview_open", v ? "1" : "0"); }} className={`rounded-sm h-8 ${previewOpen ? "text-primary" : ""}`} title={t("Preview do ar")} data-testid="studio-preview-btn"><MonitorPlay className="!h-5 !w-5" /></Button>
          <Button variant="ghost" size="sm" onClick={() => window.open(publicUrl, "_blank")} className="rounded-sm h-8" data-testid="open-output-btn"><ExternalLink className="!h-5 !w-5" /></Button>
          {can("youtube_live") && <Button variant="ghost" size="sm" onClick={() => setYtOpen(true)} className="rounded-sm h-8 relative" title={ytConfig.connected ? t("YouTube conectado") : "YouTube"} data-testid="youtube-btn"><YouTubeIcon variant={ytConfig.connected ? "red" : "white"} /></Button>}
          {can("twitch_live") && <Button variant="ghost" size="sm" onClick={() => setTwitchOpen(true)} className="rounded-sm h-8 relative" title={twitchConfig.connected ? t("Twitch conectada") : "Twitch"} data-testid="twitch-btn"><TwitchIcon active={!!twitchConfig.connected} /></Button>}
          {can("link_operador") && (
            <Button variant="ghost" size="sm" onClick={() => setOperatorOpen(true)}
              className={`rounded-sm h-8 relative ${operatorsOnline ? "text-amber-400 hover:text-amber-300" : ""}`}
              title={operatorsOnline ? t(operatorsOnline === 1 ? "Link do operador · 1 operador online" : "Link do operador · {n} operadores online", { n: operatorsOnline }) : t("Link do operador")}
              data-testid="operator-btn" data-online={operatorsOnline ? "true" : "false"}>
              <Headphones className="!h-5 !w-5" />
              {operatorsOnline > 0 && <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" aria-hidden="true" />}
            </Button>
          )}
          {can("google_sheets") && <Button variant="ghost" size="sm" onClick={() => setSheetsOpen(true)} className={`rounded-sm h-8 ${sheetsConfig.connected ? "text-emerald-400" : ""}`} title="Google Sheets" data-testid="sheets-btn"><Sheet className="!h-5 !w-5" /></Button>}
          {can("fontes_personalizadas") && <Button variant="ghost" size="sm" onClick={() => setFontsOpen(true)} className="rounded-sm h-8" title={t("Gerenciar fontes")} data-testid="fonts-btn"><Baseline className="!h-5 !w-5" /></Button>}
          <Button variant="ghost" size="sm" onClick={regenerate} className="rounded-sm h-8" data-testid="regen-token-btn"><RefreshCw className="!h-5 !w-5" /></Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="rounded-sm h-8" title={t("Formato do overlay")} data-testid="format-btn"><Ratio className="!h-5 !w-5" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => changeFormat(1920, 1080)} data-testid="format-169">16:9 · 1920×1080</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeFormat(1080, 1920)} data-testid="format-916">{t("Vertical 9:16 · 1080×1920")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeFormat(1080, 1080)} data-testid="format-11">{t("Quadrado 1:1 · 1080×1080")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="secondary" size="sm" onClick={() => setSetupOpen(true)} className="rounded-sm h-8" data-testid="setup-btn"><HelpCircle className="!h-5 !w-5 mr-1" />OBS/vMix</Button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Left sidebar (edit only — hidden on mobile) */}
        <aside className="w-72 border-r border-border/60 bg-card hidden md:flex flex-col flex-shrink-0 overflow-hidden">
          <div className="p-2 border-b border-border/60 space-y-2">
            {/* Ordem: texto · formas · YouTube · Twitch · chat ao vivo. Formas e chat ficam em menus. */}
            <Button onClick={() => addElement(defaultTextElement(elements.length))} variant="secondary" className="w-full rounded-sm h-8 text-xs" data-testid="add-text-btn">
              <Type className="h-3.5 w-3.5 mr-1.5" /> {t("Adicionar texto")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="w-full rounded-sm h-8 text-xs relative" data-testid="add-shape-menu">
                  <Shapes className="h-3.5 w-3.5 mr-1.5" /> {t("Adicionar formas")}
                  <ChevronDown className="h-3.5 w-3.5 absolute right-2 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width] min-w-[200px]">
                {SHAPE_ITEMS.map(([kind, Icon, label, testid]) => (
                  <DropdownMenuItem key={kind} onClick={() => addElement(defaultShapeElement(elements.length, kind))} className="text-xs" data-testid={testid}>
                    <Icon className="h-3.5 w-3.5 mr-2" /> {t(label)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {can("youtube_live") && <Button onClick={() => addElement(defaultDynamicElement(elements.length))} variant="secondary" className="w-full rounded-sm h-8 text-xs" data-testid="add-dynamic-btn">
              <YouTubeIcon className="mr-1.5" /> {t("Dado do YouTube")}
            </Button>}
            {can("twitch_live") && <Button onClick={() => addElement(defaultTwitchElement(elements.length))} variant="secondary" className="w-full rounded-sm h-8 text-xs" data-testid="add-twitch-btn">
              <TwitchIcon className="mr-1.5" /> {t("Dado da Twitch")}
            </Button>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="w-full rounded-sm h-8 text-xs relative" data-testid="add-chat-menu">
                  <MessagesSquare className="h-3.5 w-3.5 mr-1.5" /> {t("Chat ao vivo")}
                  <ChevronDown className="h-3.5 w-3.5 absolute right-2 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width] min-w-[200px]">
                <DropdownMenuItem onClick={() => addElement(defaultChatFeedElement(elements.length))} className="text-xs" data-testid="add-chat-btn">
                  <MessagesSquare className="h-3.5 w-3.5 mr-2" /> {t("Chat (mensagens)")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addElement(defaultCommentElement(elements.length))} className="text-xs" data-testid="add-comment-btn">
                  <Quote className="h-3.5 w-3.5 mr-2" /> {t("Destaque")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addElement(defaultSuperChatElement(elements.length))} className="text-xs" data-testid="add-superchat-btn">
                  <Sparkles className="h-3.5 w-3.5 mr-2" /> Super Chat
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* Camadas é a aba principal: sempre aberta ao entrar no editor. */}
          <Tabs defaultValue="layers" className="flex flex-col flex-1 min-h-0">
            <TabsList className="grid grid-cols-2 m-2 flex-shrink-0">
              <TabsTrigger value="layers" data-testid="tab-layers">{t("Camadas")}</TabsTrigger>
              <TabsTrigger value="media" data-testid="tab-media">{t("Mídias")}</TabsTrigger>
            </TabsList>
            <TabsContent value="media" className="flex-1 min-h-0 mt-0 overflow-hidden">
              <MediaLibrary media={library} onUpload={handleUpload} onAdd={addMedia} onDelete={deleteMedia} onReprocess={reprocessMedia} uploading={uploading} />
            </TabsContent>
            <TabsContent value="layers" className="flex-1 min-h-0 mt-0 overflow-hidden">
              <LayersPanel elements={elements} selectedId={selectedId} onSelect={select} multiIds={multiIds} onToggleMulti={toggleMulti} onChange={updateElement} onReorder={reorder} onReorderGroup={reorderGroup} onDelete={removeElement} onDuplicate={duplicateElement} />
            </TabsContent>
          </Tabs>
        </aside>

        {/* Canvas (edit only — hidden on mobile) */}
        <div ref={previewAreaRef} className="hidden md:flex flex-1 min-h-0 relative">
          <Canvas elements={elements} media={media} dims={{ w: overlay.largura, h: overlay.altura }} selectedId={selectedId} onSelect={select} multiIds={multiIds} onToggleMulti={toggleMulti} onChange={updateElement} onChangeMany={updateElements} onDuplicate={duplicateElement} ytData={ytData} twitchData={twitchData} sheetValues={sheetValues} triggerKeys={triggerKeys.current} />
          {previewOpen && (
            <div
              ref={previewRef}
              className="absolute z-40 w-[380px] max-w-[45%] rounded-md border border-border bg-card shadow-[0_16px_48px_-12px_rgba(0,0,0,0.6)] overflow-hidden"
              style={previewPos ? { left: previewPos.x, top: previewPos.y } : { right: 16, bottom: 16 }}
              data-testid="studio-preview"
            >
              <div onMouseDown={startPreviewDrag} className="flex items-center justify-between px-2.5 py-1.5 bg-secondary/60 border-b border-border/60 cursor-move select-none" data-testid="studio-preview-drag">
                <span className="text-[10px] font-mono tracking-[0.2em] text-foreground/80 flex items-center gap-1.5">
                  <MonitorPlay className="h-3.5 w-3.5 text-primary" /> {t("PREVIEW · NO AR")}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-[10px] text-red-400 flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500 live-dot" /> {t("AO VIVO")}
                  </span>
                  <button onMouseDown={(e) => e.stopPropagation()} onClick={() => { setPreviewOpen(false); localStorage.setItem("studio_preview_open", "0"); }} className="text-muted-foreground hover:text-foreground" data-testid="studio-preview-close">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
              <div className="relative w-full checker-bg" style={{ aspectRatio: `${overlay.largura || 1920} / ${overlay.altura || 1080}` }}>
                <iframe
                  src={publicUrl}
                  title={t("Preview do overlay no ar")}
                  className="absolute inset-0 w-full h-full border-0 pointer-events-none"
                  data-testid="studio-preview-iframe"
                />
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar (edit + live — hidden on mobile) */}
        <aside className="w-80 border-l border-border/60 bg-card hidden md:flex flex-col flex-shrink-0">
          <Tabs defaultValue="props" className="flex flex-col h-full">
            <TabsList className="grid grid-cols-2 m-2 flex-shrink-0">
              <TabsTrigger value="props" data-testid="tab-props">{t("Propriedades")}</TabsTrigger>
              <TabsTrigger value="live" data-testid="tab-live">{t("Ao Vivo")}</TabsTrigger>
            </TabsList>
            <TabsContent value="props" className="flex-1 min-h-0 mt-0">
              {multiIds.length > 1 ? (
                <MultiPanel
                  members={elements.filter((x) => multiIds.includes(x.id))}
                  dims={{ w: overlay.largura, h: overlay.altura }}
                  onChangeMany={updateElements}
                  onClear={() => setMultiIds([])}
                  onDelete={() => removeMany(multiIds)}
                />
              ) : selGroupName && selGroupMembers.length ? (
                <GroupPanel
                  name={selGroupName}
                  members={selGroupMembers}
                  color={groupColors[selGroupName]}
                  groups={groups}
                  dims={{ w: overlay.largura, h: overlay.altura }}
                  onRename={(v) => renameGroup(selGroupName, v)}
                  onUngroup={() => ungroup(selGroupName)}
                  onChangeMany={updateElements}
                  onAnim={(anim) => setGroupAnim(selGroupName, anim)}
                />
              ) : (
                <PropertiesPanel element={selected} onChange={updateElement} groups={groups} fonts={fonts.map((f) => f.family)} dims={{ w: overlay.largura, h: overlay.altura }} />
              )}
            </TabsContent>
            <TabsContent value="live" className="flex-1 min-h-0 mt-0">
              <LivePanel
                elements={elements}
                connected={connected}
                sources={sources}
                ytData={ytData}
                twitchData={twitchData}
                chatEnabled={chatEnabled}
                chatDemo={chatDemo}
                chatSource={chatSource}
                chatConn={{ youtube: !!ytConfig.connected, twitch: !!twitchConfig.connected }}
                chatMessages={chatMessages}
                chatPinned={chatPinned}
                onChatStart={startChat}
                onChatStop={stopChat}
                onChatPin={pinMsg}
                onChatUnpin={unpinMsg}
                onChatReject={rejectMsg}
                onChatAlert={alertMsg}
                onToggle={(elId, v) => updateElement(elId, { visivel: v })}
                onChangeText={(elId, text) => updateElement(elId, { conteudo: text })}
                onUpdate={updateElement}
                onToggleGroup={toggleGroup}
                onTrigger={(elId) => { send({ type: "trigger", elementId: elId }); triggerKeys.current[elId] = (triggerKeys.current[elId] || 0) + 1; forceTick((n) => n + 1); }}
                onHideAll={hideAll}
              />
            </TabsContent>
          </Tabs>
        </aside>

        {/* Mobile: only the Live Control Panel is visible */}
        <aside className="flex md:hidden flex-1 flex-col bg-card min-h-0" data-testid="mobile-live-panel">
          <div className="px-3 py-2 text-[11px] text-muted-foreground border-b border-border/60 flex items-center gap-2">
            <Smartphone className="h-3.5 w-3.5" /> {t("Para editar, use tablet ou desktop")}
          </div>
          <LivePanel
            elements={elements}
            connected={connected}
            sources={sources}
            ytData={ytData}
            twitchData={twitchData}
            chatEnabled={chatEnabled}
            chatDemo={chatDemo}
            chatSource={chatSource}
            chatConn={{ youtube: !!ytConfig.connected, twitch: !!twitchConfig.connected }}
            chatMessages={chatMessages}
            chatPinned={chatPinned}
            onChatStart={startChat}
            onChatStop={stopChat}
            onChatPin={pinMsg}
            onChatUnpin={unpinMsg}
            onChatReject={rejectMsg}
            onChatAlert={alertMsg}
            onToggle={(elId, v) => updateElement(elId, { visivel: v })}
            onChangeText={(elId, text) => updateElement(elId, { conteudo: text })}
            onUpdate={updateElement}
            onToggleGroup={toggleGroup}
            onTrigger={(elId) => { send({ type: "trigger", elementId: elId }); triggerKeys.current[elId] = (triggerKeys.current[elId] || 0) + 1; forceTick((n) => n + 1); }}
            onHideAll={hideAll}
          />
        </aside>
      </div>

      <GroupDialog
        open={groupDlg}
        onOpenChange={setGroupDlg}
        target={selGroupName ? { kind: "group", name: selGroupName } : (selected ? { kind: "layer", el: selected } : null)}
        groups={groups}
        colors={groupColors}
        onApply={(name) => (selGroupName ? renameGroup(selGroupName, name) : (setGroupOf([selected.id], name), setSelectedId(groupSelId(name))))}
        onRemove={() => (selGroupName ? ungroup(selGroupName) : setGroupOf([selected.id], null))}
      />
      <SetupModal open={setupOpen} onOpenChange={setSetupOpen} url={publicUrl} />
      <FontsModal open={fontsOpen} onOpenChange={setFontsOpen} fonts={fonts} onChange={loadFonts} />
      <OperatorModal open={operatorOpen} onOpenChange={setOperatorOpen} overlayId={id} />
      <SheetsModal open={sheetsOpen} onOpenChange={setSheetsOpen} overlayId={id} config={sheetsConfig} setConfig={setSheetsConfig} />
      <YouTubePanel open={ytOpen} onOpenChange={setYtOpen} overlayId={id} config={ytConfig} setConfig={setYtConfig} data={ytData} setData={setYtData} />
      <TwitchModal open={twitchOpen} onOpenChange={setTwitchOpen} overlayId={id} config={twitchConfig} setConfig={setTwitchConfig} data={twitchData} setData={setTwitchData} />
    </div>
  );
}
